import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Bell, CheckCheck, Loader2, Trash2, BookOpen, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pt-br';
import { useNotifications, Notification } from '@/hooks/useNotifications';
import { generateChapterLink, generateWorkLink } from '@/utils/utils';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');

interface NotificationsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const FILTERS = [
    { id: 'all', label: 'Todas', icon: Bell },
    { id: 'novos_capitulos', label: 'Capítulos', icon: BookOpen },
    { id: 'comentarios', label: 'Conversas', icon: MessageCircle },
] as const;

type FilterId = typeof FILTERS[number]['id'];

export default function NotificationsModal({ isOpen, onClose }: NotificationsModalProps) {
    const navigate = useNavigate();
    const {
        notifications,
        loading,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        deleteReadNotifications,
        fetchUnreadCount
    } = useNotifications();
    const [markingAll, setMarkingAll] = useState(false);
    const [deletingLidas, setDeletingLidas] = useState(false);
    const [filter, setFilter] = useState<FilterId>('all');

    useEffect(() => {
        if (isOpen) {
            fetchNotifications(1, filter === 'all' ? undefined : filter);
            fetchUnreadCount();
        }
    }, [isOpen, filter]);

    const handleNotificationClick = async (notificacao: Notification) => {
        if (!notificacao.lida) markAsRead(notificacao.id);

        if (notificacao.dados) {
            let dadosParsed;
            try {
                dadosParsed = typeof notificacao.dados === 'string'
                    ? JSON.parse(notificacao.dados)
                    : notificacao.dados;
            } catch (e) { return; }

            const rota = dadosParsed.rota;
            const kind = dadosParsed.kind;
            let params = dadosParsed.params;

            if (typeof params === 'string') {
                try { params = JSON.parse(params); } catch (e) { }
            }

            onClose();

            setTimeout(() => {
                const comentarioId = params?.comentario?.id || params?.comentario_id || params?.comentarioId;
                const commentQuery = comentarioId ? `?comentario=${comentarioId}` : '';
                const chapterId = params?.capitulo?.id || params?.capitulo_id || params?.chapterId;
                const obraId = params?.obra?.id || params?.obra_id || params?.obraId || params?.id;

                if ((rota === 'capitulo' || kind === 'cap_novo') && chapterId) {
                    navigate(generateChapterLink(chapterId) + commentQuery);
                } else if (rota === 'planos' || kind === 'vip_ativado') {
                    navigate('/landing');
                } else if (rota === 'seguir' && (params?.usuario?.id || params?.usuario_id)) {
                    navigate(`/perfil/${params?.usuario?.id || params?.usuario_id}`);
                } else if (rota === 'obra' && obraId) {
                    navigate(generateWorkLink(obraId) + commentQuery);
                } else if (rota === 'comentarios') {
                    if (chapterId) {
                        navigate(generateChapterLink(chapterId) + commentQuery);
                    } else if (obraId) {
                        navigate(generateWorkLink(obraId) + commentQuery);
                    } else if (rota === 'lista' && params?.lista_id) {
                        navigate(`/listas/${params.lista_id}`);
                    }
                }
            }, 100);
        }
    };

    const handleMarkAllRead = async () => {
        if (markingAll) return;
        setMarkingAll(true);
        try { await markAllAsRead(); } finally { setMarkingAll(false); }
    };

    const handleDeleteRead = async () => {
        if (deletingLidas || !window.confirm('Excluir todas as notificações lidas?')) return;
        setDeletingLidas(true);
        try { await deleteReadNotifications(); } finally { setDeletingLidas(false); }
    };

    const formatDate = (dateString: string) => {
        const date = dayjs(dateString);
        const now = dayjs();
        if (now.diff(date, 'hour') < 24) return date.fromNow();
        return date.format('DD [de] MMM');
    };

    const hasUnread = notifications.some(n => !n.lida);
    const hasRead = notifications.some(n => n.lida);

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm"
                        onClick={onClose}
                    />

                    <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                        className="fixed bottom-0 left-0 right-0 z-[10000] bg-neutral-900 border-t border-white/10 rounded-t-3xl shadow-2xl max-h-[85vh] w-full flex flex-col"
                    >
                        {/* Handle Bar */}
                        <div className="flex justify-center pt-3 pb-2">
                            <div className="w-12 h-1.5 bg-white/20 rounded-full" />
                        </div>

                        {/* Header */}
                        <div className="flex items-center justify-between px-5 pb-4 border-b border-white/5">
                            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                                <Bell size={18} className="text-white/50" />
                                Notificações
                            </h2>
                            <button
                                onClick={onClose}
                                className="p-2 rounded-lg transition-colors text-white/40 hover:text-white hover:bg-white/5 cursor-pointer"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto px-4 pb-4">
                            {/* Filter pills */}
                            <div className="flex gap-2 my-4 overflow-x-auto pb-1 scrollbar-hide">
                                {FILTERS.map(({ id, label, icon: Icon }) => (
                                    <button
                                        key={id}
                                        onClick={() => setFilter(id)}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                                            filter === id
                                                ? 'bg-white/10 text-white'
                                                : 'bg-white/5 text-white/50 hover:bg-white/5 hover:text-white/70'
                                        }`}
                                    >
                                        <Icon size={12} />
                                        {label}
                                    </button>
                                ))}
                            </div>

                            {/* Actions row */}
                            <div className="flex items-center justify-between mb-4">
                                <button
                                    onClick={handleDeleteRead}
                                    disabled={deletingLidas || !hasRead}
                                    className="flex items-center gap-1.5 text-xs text-white/40 hover:text-red-400 disabled:opacity-30 transition-colors"
                                >
                                    {deletingLidas
                                        ? <Loader2 size={13} className="animate-spin" />
                                        : <Trash2 size={13} />
                                    }
                                    Limpar lidas
                                </button>
                                <button
                                    onClick={handleMarkAllRead}
                                    disabled={markingAll || !hasUnread}
                                    className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white disabled:opacity-30 transition-colors"
                                >
                                    {markingAll
                                        ? <Loader2 size={13} className="animate-spin" />
                                        : <CheckCheck size={13} />
                                    }
                                    Marcar lidas
                                </button>
                            </div>

                            {loading && notifications.length === 0 ? (
                                <div className="py-20 text-center text-white/40">
                                    <Loader2 size={28} className="mx-auto animate-spin mb-3 text-white/30" />
                                    <p className="text-sm">Carregando...</p>
                                </div>
                            ) : notifications.length === 0 ? (
                                <div className="py-16 text-center">
                                    <Bell size={40} className="mx-auto text-white/10 mb-3" />
                                    <p className="text-white/50 text-sm">Nenhuma notificação</p>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-1">
                                    {notifications.map((notificacao) => (
                                        <div
                                            key={notificacao.id}
                                            onClick={() => handleNotificationClick(notificacao)}
                                            className={`
                                                relative p-3 rounded-lg cursor-pointer transition-colors flex gap-3
                                                ${!notificacao.lida ? 'bg-white/5 active:bg-white/[0.07]' : 'active:bg-white/[0.03]'}
                                            `}
                                        >
                                            {!notificacao.lida && (
                                                <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-white/60 flex-shrink-0" />
                                            )}
                                            {(notificacao.capa || notificacao.imagem) && (
                                                <div className={`shrink-0 overflow-hidden border border-white/10 bg-white/5 ${
                                                    notificacao.imagem && !notificacao.capa
                                                        ? 'w-9 h-9 rounded-full'
                                                        : 'w-9 h-9 rounded-md'
                                                }`}>
                                                    <img
                                                        src={notificacao.capa || notificacao.imagem}
                                                        alt=""
                                                        className="h-full w-full object-cover"
                                                    />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-2 mb-0.5">
                                                    <h4 className={`text-sm leading-tight ${!notificacao.lida ? 'font-medium text-white' : 'font-normal text-white/80'}`}>
                                                        {notificacao.titulo}
                                                    </h4>
                                                    <span className="text-[10px] text-white/30 whitespace-nowrap shrink-0 mt-0.5">
                                                        {formatDate(notificacao.data)}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-white/50 line-clamp-2">
                                                    {notificacao.mensagem}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
